import axios from 'axios'
import {useEffect, useMemo, useState} from 'react'

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'

import SearchIcon from '@mui/icons-material/Search'

import {DataGridPro, GridColDef} from '@mui/x-data-grid-pro'

import {chargeBackHost} from 'GLOBAL/envVariables'
import {useSelector} from 'react-redux'
import {showChargeAlert, showChargeConfirmAlert} from 'utils/charge/ChargeAlert'

type RowType = {
  id: string
  customer_number: string
  customer_name: string
  address_road: string
  gum_name: string
}

type GumdamType = {
  gum_code: string
  gum_name: string
  used_status: boolean
}

const CustomerTransfer = () => {
  const officeValue = useSelector((state: any) => state?.officeInfo.value)

  const [rows, setRows] = useState<RowType[]>([])
  const [gumList, setGumList] = useState<GumdamType[]>([])
  const [gumCode, setGumCode] = useState<string>('')

  const [loading, setLoading] = useState(false)
  const [pageSize, setPageSize] = useState(30)
  const [searchText, setSearchText] = useState('')

  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [openTransferModal, setOpenTransferModal] = useState(false)
  const [targetGumCode, setTargetGumCode] = useState('')

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'customer_number',
        headerName: '수용가번호',
        flex: 1,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'customer_name',
        headerName: '수용가명',
        flex: 1,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'address_road',
        headerName: '주소',
        flex: 2,
        headerAlign: 'center',
      },
      {
        field: 'gum_name',
        headerName: '검침원',
        flex: 1,
        align: 'center',
        headerAlign: 'center',
      },
    ],
    []
  )

  /**
   * 검침원 목록 조회
   */
  const fetchGumdam = async () => {
    try {
      const res = await axios.get(`${chargeBackHost}/api/gumdam/select?office_id=${officeValue}`)
      const data = res.data.data || []

      setGumList(data)

      if (data.length > 0) {
        setGumCode(data[0].gum_code)
      }
    } catch (err) {
      console.error('GUMDAM LOAD ERROR', err)

      await showChargeAlert({
        title: '조회 실패',
        text: '검침원 목록을 불러오는 중 오류가 발생했습니다.',
        icon: 'error',
      })
    }
  }

  /**
   * 수용가 조회
   */
  const fetchCustomers = async () => {
    if (!gumCode) return

    try {
      setLoading(true)

      const res = await axios.get(
        `${chargeBackHost}/api/customer/select?office_id=${officeValue}&gum_code=${gumCode}`
      )

      const data = res.data.data || []

      const mapped = data.map((v: any) => ({
        id: v.customer_number,
        ...v,
      }))

      setRows(mapped)
    } catch (err) {
      console.error('CUSTOMER LOAD ERROR', err)

      await showChargeAlert({
        title: '조회 실패',
        text: '수용가 목록을 불러오는 중 오류가 발생했습니다.',
        icon: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchGumdam()
  }, [])

  useEffect(() => {
    fetchCustomers()
  }, [gumCode])

  /**
   * 검색 필터
   */
  const filteredRows = useMemo(() => {
    if (!searchText) return rows

    const keyword = searchText.toLowerCase()

    return rows.filter((row) => Object.values(row).join(' ').toLowerCase().includes(keyword))
  }, [rows, searchText])

  const handleOpenTransferModal = async () => {
    if (selectedRows.length === 0) {
      await showChargeAlert({
        title: '선택 필요',
        text: '변경할 수용가를 먼저 선택해주세요.',
        icon: 'warning',
      })
      return
    }

    setTargetGumCode('')
    setOpenTransferModal(true)
  }

  const handleTransfer = async () => {
    if (!targetGumCode) {
      await showChargeAlert({
        title: '선택 필요',
        text: '변경할 검침원을 선택해주세요.',
        icon: 'warning',
      })
      return
    }

    if (targetGumCode === gumCode) {
      await showChargeAlert({
        title: '변경 불가',
        text: '현재 검침원과 동일한 검침원으로는 변경할 수 없습니다.',
        icon: 'warning',
      })
      return
    }

    const targetGum = gumList.find((g) => g.gum_code === targetGumCode)
    const targetGumName = targetGum?.gum_name || ''

    /**
     * Dialog 먼저 닫고 확인 얼럿 표시
     * 겹침(z-index) 문제 방지
     */
    setOpenTransferModal(false)

    const result = await showChargeConfirmAlert({
      title: '검침원 변경 확인',
      text: `선택된 수용가 ${selectedRows.length}건을 "${targetGumName}" 검침원으로 변경하시겠습니까?`,
      icon: 'warning',
      confirmButtonText: '변경',
      cancelButtonText: '취소',
    })

    if (!result.isConfirmed) {
      setOpenTransferModal(true)
      return
    }

    try {
      setLoading(true)

      await axios.post(`${chargeBackHost}/api/gumcode/transfer`, {
        office_id: officeValue,
        from_gum_code: gumCode,
        to_gum_code: targetGumCode,
        customer_numbers: selectedRows,
      })

      setSelectedRows([])
      setTargetGumCode('')
      await fetchCustomers()

      await showChargeAlert({
        title: '변경 완료',
        text: `선택된 수용가 ${selectedRows.length}건의 검침원이 변경되었습니다.`,
        icon: 'success',
      })
    } catch (err: any) {
      console.error('TRANSFER ERROR', err)

      await showChargeAlert({
        title: '변경 실패',
        text:
          err?.response?.data?.message ||
          err?.response?.data?.response ||
          '검침원 변경 중 오류가 발생했습니다.',
        icon: 'error',
      })

      setOpenTransferModal(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box display='flex' flexDirection='column' height='100%'>
      <Box display='flex' justifyContent='space-between' alignItems='center' sx={{mb: 2}}>
        <Box>
          <Typography variant='h6'>검침원 변경</Typography>
          <Typography
            variant='body2'
            sx={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'text.secondary',
            }}
          >
            수용가에 등록되어 있는 검침원을 변경합니다.
          </Typography>
        </Box>

        <Box display='flex' gap={2} alignItems='center'>
          <FormControl size='small' sx={{width: 180}}>
            <InputLabel>검침원</InputLabel>
            <Select
              value={gumCode}
              label='검침원'
              onChange={(e) => {
                setGumCode(e.target.value)
                setSelectedRows([])
              }}
              renderValue={(selected) => {
                if (selected === 'no') return '미할당'

                const found = gumList.find((g) => g.gum_code === selected)
                return found ? found.gum_name : ''
              }}
            >
              {gumList.map((g) => (
                <MenuItem key={g.gum_code} value={g.gum_code}>
                  <Box display='flex' justifyContent='space-between' width='100%'>
                    <span>{g.gum_name}</span>

                    <Typography
                      variant='body2'
                      sx={{
                        color: g.used_status ? 'success.main' : 'error.main',
                        fontWeight: 500,
                        alignContent: 'end',
                      }}
                    >
                      {g.used_status ? '사용' : '미사용'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}

              <MenuItem value='no'>미할당</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size='small'
            placeholder='검색'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{width: 350}}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant='contained'
            color='primary'
            disabled={selectedRows.length === 0}
            onClick={handleOpenTransferModal}
          >
            검침원 변경
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <DataGridPro
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(size) => setPageSize(size)}
          rowsPerPageOptions={[10, 30, 50, 100]}
          checkboxSelection
          disableSelectionOnClick
          selectionModel={selectedRows}
          onSelectionModelChange={(ids) => setSelectedRows(ids as string[])}
          sx={{
            '& .MuiDataGrid-root, & .MuiDataGrid-columnHeaders': {
              fontFamily: 'Noto Sans KR, sans-serif',
            },
            '& .MuiDataGrid-columnHeaders': {backgroundColor: '#f3f4f6'},
            '& .MuiDataGrid-cell': {fontSize: '16px', fontWeight: '400'},
            '& .MuiDataGrid-pinnedColumns': {backgroundColor: '#f9fafb'},
            '& .MuiDataGrid-pinnedColumnHeaders': {backgroundColor: '#f3f4f6'},
            '& .edited-row': {backgroundColor: '#e9f7ef'},
            '& .MuiDataGrid-row.warning-row': {backgroundColor: '#fef9e7'},
            '& .MuiDataGrid-cell:focus': {outline: 'none'},
            '& .MuiDataGrid-cell:focus-within': {outline: 'none'},
          }}
        />
      </Paper>

      <Dialog
        open={openTransferModal}
        onClose={() => setOpenTransferModal(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>검침원 변경</DialogTitle>

        <DialogContent>
          <FormControl fullWidth sx={{mt: 2}}>
            <InputLabel>변경할 검침원</InputLabel>
            <Select
              value={targetGumCode}
              label='변경할 검침원'
              onChange={(e) => setTargetGumCode(e.target.value)}
            >
              {gumList
                .filter((g) => g.gum_code !== gumCode)
                .map((g) => (
                  <MenuItem key={g.gum_code} value={g.gum_code}>
                    {g.gum_name}
                  </MenuItem>
                ))}

              {gumCode !== 'no' && <MenuItem value='no'>미할당</MenuItem>}
            </Select>
          </FormControl>

          <Typography sx={{mt: 2}} color='text.secondary'>
            선택된 수용가 : {selectedRows.length}건
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenTransferModal(false)}>취소</Button>

          <Button variant='contained' disabled={!targetGumCode} onClick={handleTransfer}>
            변경
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CustomerTransfer
